import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import "./landing.css";
import "./service-detail.css";
import serviceMeta from "../data/serviceMeta.json";
import { trackAdsConversion } from "../lib/gads";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = serviceMeta.site.url;

// Build lookup maps from the shared JSON
const PILL_DESC = Object.fromEntries(
  Object.entries(serviceMeta.services).map(([slug, s]) => [slug, s.pillDesc])
);
const SEO = Object.fromEntries(
  Object.entries(serviceMeta.services).map(([slug, s]) => [
    slug,
    { seoTitle: s.seoTitle, seoDescription: s.seoDescription, keywords: s.keywords },
  ])
);
const RELATED = Object.fromEntries(
  Object.entries(serviceMeta.services).map(([slug, s]) => [slug, s.related || []])
);

// Small helper to upsert a <meta> tag by name or property
function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);

// Icons reused as accents
const I = {
  file: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  bldg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" /></svg>,
  card: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></svg>,
  stamp: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="9" r="4" /><path d="M12 13v8M8 21h8" /></svg>,
  up: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  globe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>,
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>,
  cloud: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>,
  ai: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg>,
};

const SERVICE_DETAILS = {
  "instant-pnl": {
    icon: I.up,
    eyebrow: "48-Hour Turnaround",
    title: "Instant P&L from Bank Statements",
    tagline: "Upload bank statements. Get an audit-grade, tax-ready P&L reconciled to the penny — categorized by AI, reviewed by our CPAs.",
    paragraphs: [
      "For founders who need to close a quarter fast or lenders asking for financials by Friday: send us your bank PDFs or CSVs and receive a clean Profit & Loss statement within 48 hours. Our engine parses every transaction, applies your prior categorizations, and reconciles totals against every statement page to a zero-variance close.",
      "Every P&L is reviewed and signed off by a licensed CPA before delivery. You receive the workbook (Excel + PDF), an audit-binder trail, and a Form 4562 depreciation feed ready to hand your tax preparer.",
    ],
    highlights: [
      "PDF & CSV bank statement intake",
      "AI vendor categorization with per-client memory",
      "Zero-variance reconciliation to statement totals",
      "CPA-reviewed before every delivery",
      "Excel workbook + audit binder PDF export",
      "Form 4562 depreciation feed included",
    ],
    testimonial: {
      quote: "Six months of chaotic Chase and Amex activity turned into a clean P&L in under two days. Their CPA caught a $12K miscategorization our bookkeeper had missed for a year.",
      author: "R. Patel",
      role: "Founder, DFW e-commerce brand",
    },
    ctaText: "Upload your statements",
  },
  "tax-strategy": {
    icon: I.file,
    eyebrow: "Tax Services",
    title: "Tax Strategy & Compliance",
    tagline: "Stop filing taxes. Start planning them. We identify legal reduction strategies before the year ends — not after it's too late.",
    paragraphs: [
      "Individual and business tax returns, federal and multi-state, prepared by licensed CPAs who understand your operating reality — not just your 1099s. We ask the questions your last preparer didn't and build a return that reflects a defensible, long-term tax position.",
      "Beyond preparation, we run quarterly strategy sessions to model estimated payments, entity-level elections, retirement contribution timing, and year-end moves that reduce your effective rate before December 31.",
    ],
    highlights: [
      "Form 1040, 1120, 1120-S, 1065 preparation",
      "Federal + multi-state return coordination",
      "Quarterly estimated tax planning",
      "Entity-election analysis (S-Corp, C-Corp, partnership)",
      "IRS and state notice representation",
      "Amended returns and prior-year cleanup",
    ],
    testimonial: {
      quote: "Artha's proactive tax planning saved us $47K over two years vs. our previous CPA. They actually pick up the phone before I make big decisions.",
      author: "M. Chen",
      role: "Owner, Frisco medical practice",
    },
    ctaText: "Book a tax strategy call",
  },
  "financial-advisory": {
    icon: I.chart,
    eyebrow: "Financial Advisory",
    title: "Financial Advisory",
    tagline: "Strategic growth consulting, M&A due diligence, business valuations, and GAAP-compliant financial reporting — built for owners preparing to scale, transact, or exit.",
    paragraphs: [
      "Whether you're raising capital, evaluating an acquisition, or building a defensible valuation for a partner buy-out, our advisory engagements start with a rigorous financial diagnostic — cash conversion cycle, unit economics, working-capital drag, and gross-margin decomposition.",
      "From there we build the financial model, the data room, and the narrative your investors, buyers, or bankers actually want to see. GAAP-compliant statements and reviewed financials available on request.",
    ],
    highlights: [
      "Business valuation reports (409A, buy-sell, gift/estate)",
      "M&A buy-side and sell-side due diligence",
      "3-statement financial models",
      "Capital raise data room and diligence prep",
      "GAAP-compliant reviewed financial statements",
      "Post-transaction integration accounting",
    ],
    testimonial: {
      quote: "They built the model that closed our Series A. The lead investor's diligence team said it was the cleanest founder-prepared deck they'd seen all year.",
      author: "S. Iyer",
      role: "CEO, DFW SaaS startup",
    },
    ctaText: "Discuss your engagement",
  },
  "estate-trust-planning": {
    icon: I.shield,
    eyebrow: "Wills & Trust Services",
    title: "Estate & Trust Planning",
    tagline: "Protect intergenerational wealth with trust structures, gifting strategies, and succession roadmaps designed and coordinated with your estate attorney.",
    paragraphs: [
      "Estate planning is a two-professional problem: your attorney drafts the documents, and your CPA models the tax and cash-flow consequences. We sit on the tax side — running the projections, executing the annual gift-tax filings (Form 709), and maintaining the trust and estate income-tax returns (Form 1041) after the plan is funded.",
      "For business owners, we coordinate the ownership-transfer mechanics: grantor trusts, IDGT sales, family limited partnerships, and step-up planning — all sequenced against your income-tax situation year by year.",
    ],
    highlights: [
      "Form 1041 fiduciary income-tax returns",
      "Form 709 gift-tax returns",
      "Grantor trust and IDGT structuring",
      "Family limited partnership valuations",
      "Step-up-in-basis planning",
      "Business succession roadmaps",
    ],
    testimonial: {
      quote: "Artha coordinated the tax side of our family limited partnership rollout flawlessly. Every 709 filing landed on time and matched exactly what our attorney had drafted.",
      author: "L. Thompson",
      role: "Third-generation family-business owner",
    },
    ctaText: "Plan your legacy",
  },
  "business-formation": {
    icon: I.bldg,
    eyebrow: "Formation & Structuring",
    title: "Business Formation & Structuring",
    tagline: "Turnkey LLC, S-Corp, and C-Corp setup — Federal EIN registration, corporate kits, and governance advisory across all 50 states.",
    paragraphs: [
      "Choosing the wrong entity costs founders more than any single tax return will ever save. We model the tax impact of LLC vs. S-Corp vs. C-Corp against your projected income for the next three years, then file the state formation, obtain your EIN, and set up your corporate books before the first invoice is sent.",
      "For non-resident founders, we handle the ITIN process, Form SS-4 EIN-without-SSN filing, and the Form 5472 disclosure regime that applies to foreign-owned single-member LLCs.",
    ],
    highlights: [
      "50-state LLC, S-Corp, C-Corp formation",
      "Federal EIN (with or without SSN)",
      "S-Corp election (Form 2553) and revocation planning",
      "Foreign-owned LLC compliance (Form 5472)",
      "Registered-agent coordination",
      "Corporate-kit and governance setup",
    ],
    testimonial: {
      quote: "As a UK founder with no SSN, Artha got my Delaware C-Corp, EIN, and bank account working in three weeks. Everyone else had told me it would take six months.",
      author: "J. Bennett",
      role: "Non-resident founder, UK → US",
    },
    ctaText: "Form your company",
  },
  "payroll": {
    icon: I.card,
    eyebrow: "Payroll",
    title: "Payroll Management",
    tagline: "End-to-end payroll processing, direct deposit, tax filings, and year-end W-2 and 1099 management — for teams of 2 to 200.",
    paragraphs: [
      "We run your payroll cycle inside modern platforms (Gusto, Rippling, ADP) so your team is paid on time, every time, and every federal and state tax filing is submitted before the deadline. No missed 941s, no state unemployment surprises.",
      "Year-end, we handle the W-2 and 1099-NEC reconciliations, deliver copies to your team, and file the annual returns — with a full audit trail retained inside your Artha portal.",
    ],
    highlights: [
      "Weekly, bi-weekly, or semi-monthly cycles",
      "Direct deposit and paycard support",
      "Federal 941, 940, and state unemployment filings",
      "W-2 and 1099-NEC year-end processing",
      "S-Corp reasonable-compensation planning",
      "Multi-state payroll coordination",
    ],
    testimonial: {
      quote: "Payroll used to eat a full day every other Friday. Since Artha took it over, I don't think about it — and every filing has landed on time.",
      author: "K. Ramirez",
      role: "Founder, 14-person marketing agency",
    },
    ctaText: "Get payroll off your plate",
  },
  "notary": {
    icon: I.stamp,
    eyebrow: "Notary & Admin",
    title: "Notary Public & Administrative Support",
    tagline: "Official notary services for legal contracts, real-estate transactions, and corporate documents — available at our Frisco office and by appointment.",
    paragraphs: [
      "Our commissioned Texas notaries handle acknowledgments, jurats, and copy certifications for real-estate closings, loan documents, corporate resolutions, powers of attorney, and international apostilles. Every notarization is logged and stored in your Artha portal for future reference.",
      "For remote signers, we coordinate Remote Online Notarization (RON) sessions through state-approved platforms — no drive to the office required.",
    ],
    highlights: [
      "In-person and Remote Online Notarization (RON)",
      "Real-estate and loan-document signings",
      "Corporate resolutions and consents",
      "Powers of attorney (financial + medical)",
      "Apostille coordination for international use",
      "Notary journal stored in your client portal",
    ],
    testimonial: {
      quote: "They handled the notarization for our cross-border corporate resolution and had it apostilled within a week. Zero hassle.",
      author: "D. Alvarez",
      role: "Corporate secretary, Mexico → US subsidiary",
    },
    ctaText: "Schedule a notarization",
  },
  "fractional-cfo": {
    icon: I.chart,
    eyebrow: "Fractional CFO",
    title: "Fractional CFO",
    tagline: "Executive-level financial leadership without the full-time cost. Forecasting, KPIs, and capital planning on demand.",
    paragraphs: [
      "Growing businesses hit the CFO ceiling somewhere between $2M and $20M in revenue: you need real cash forecasting, board-ready reporting, and someone to own the numbers with you — but you don't need (or can't afford) a $250K/yr hire. Our fractional CFO engagement gives you that seat, for a fraction of the cost, on a monthly retainer.",
      "Every engagement includes a rolling 13-week cash forecast, monthly management-reporting package, KPI scorecard, and quarterly board deck — plus on-call support for banker meetings, investor updates, and pricing decisions.",
    ],
    highlights: [
      "Rolling 13-week cash-flow forecast",
      "Monthly management reporting package",
      "KPI scorecard and unit-economics tracking",
      "Quarterly board deck preparation",
      "Banker and investor meeting support",
      "Pricing and gross-margin analysis",
    ],
    testimonial: {
      quote: "I finally understand where our cash is going. The 13-week forecast changed how I make hiring decisions.",
      author: "T. Nguyen",
      role: "CEO, $4M ARR SaaS",
    },
    ctaText: "Talk to a Fractional CFO",
  },
  "real-estate-tax": {
    icon: I.home,
    eyebrow: "Real Estate Tax",
    title: "Real Estate Tax",
    tagline: "1031 exchanges, cost segregation, and passive-loss strategies — optimized for investors at every portfolio stage.",
    paragraphs: [
      "Real-estate investors leave more money on the table than any other client segment we work with. Cost-segregation studies unlock accelerated depreciation. 1031 exchanges defer capital gains indefinitely. Real-estate-professional status can flip passive losses into active offsets against W-2 income.",
      "We coordinate the whole stack: engineer-reviewed cost-seg reports, qualified-intermediary handoff for 1031s, passive-activity grouping elections, and the annual Form 8582 modeling that keeps everything defensible under audit.",
    ],
    highlights: [
      "Cost-segregation studies (engineer-reviewed)",
      "1031 like-kind exchange coordination",
      "Real-estate-professional-status planning",
      "Passive-activity grouping elections",
      "Short-term rental (STR) loophole modeling",
      "Opportunity Zone (§1400Z) compliance",
    ],
    testimonial: {
      quote: "The cost-seg on our Frisco apartment complex generated $340K of first-year depreciation. It funded our next down payment.",
      author: "H. Kaur",
      role: "Multifamily investor, 84 units",
    },
    ctaText: "Optimize your real-estate tax",
  },
  "cloud-accounting": {
    icon: I.cloud,
    eyebrow: "Cloud Accounting",
    title: "Cloud Accounting Setup",
    tagline: "Migrate to a real-time financial stack with QuickBooks Online, Gusto, and Bill.com — fully integrated and automated.",
    paragraphs: [
      "If your books close 45 days after month-end, you're driving looking in the rearview mirror. We rebuild your accounting stack around QuickBooks Online (or Xero), wire in bank feeds, Bill.com for AP, Ramp/Brex for corporate cards, Gusto for payroll, and a documented month-end close checklist that lands in five business days.",
      "Migration includes chart-of-accounts redesign, opening-balance reconciliation, historical clean-up, and a two-month parallel run so nothing falls through the cracks.",
    ],
    highlights: [
      "QuickBooks Online or Xero migration",
      "Chart-of-accounts redesign",
      "Bank & credit-card feed setup",
      "AP automation (Bill.com, Ramp)",
      "5-day month-end close cadence",
      "Historical clean-up and reconciliation",
    ],
    testimonial: {
      quote: "We closed February in 6 days for the first time in company history. Our bank finally gave us the credit line we'd been asking for.",
      author: "B. Osei",
      role: "Founder, food-service supplier",
    },
    ctaText: "Modernize your accounting stack",
  },
  "business-advisory": {
    icon: I.users,
    eyebrow: "Business Advisory",
    title: "Business Advisory",
    tagline: "From entity structure decisions to exit planning — strategic financial counsel at every stage of business growth.",
    paragraphs: [
      "Every business decision has a tax and cash-flow shadow. Should you take a distribution or a bonus? Buy the building or lease it? Bring in an equity partner or use SBA debt? Our advisory engagement puts a CPA in the room whenever you're about to make a decision with a five- or six-figure downstream impact.",
      "For owners eyeing an exit, we run a 24-to-36-month pre-transaction cleanup — normalizing EBITDA, unwinding related-party arrangements, and building the quality-of-earnings-defensible financials that maximize valuation.",
    ],
    highlights: [
      "Entity structure and reorganization analysis",
      "Owner-compensation planning",
      "Buy-vs-lease modeling",
      "SBA, conventional, and private-credit financing prep",
      "Pre-exit financial cleanup (24-36 months)",
      "Quality-of-earnings preparation",
    ],
    testimonial: {
      quote: "They walked us through the sell-side prep for 18 months. When the LOI came in, our diligence closed in 40 days — half the industry average.",
      author: "P. Sundaram",
      role: "Former owner, DFW logistics firm ($22M exit)",
    },
    ctaText: "Get strategic counsel",
  },
  "non-resident-tax": {
    icon: I.globe,
    eyebrow: "International Tax",
    title: "Non-Resident & International Tax",
    tagline: "Full US compliance for foreign nationals, expats, and cross-border businesses — every reporting obligation, handled precisely.",
    paragraphs: [
      "US international tax is where most preparers quietly punt. We don't. FBAR (FinCEN 114), FATCA (Form 8938), Form 5472 for foreign-owned LLCs, Form 5471 for controlled foreign corps, Form 8865 for foreign partnerships, ITIN applications, W-8BEN and W-8BEN-E determinations, treaty-based positions on Form 8833 — this is our core practice.",
      "We work with H-1B and L-1 visa holders, expats abroad, non-resident aliens with US real estate, and foreign entrepreneurs setting up US operations. If the IRS has a form for your situation, we've probably filed a thousand of them.",
    ],
    highlights: [
      "FBAR (FinCEN 114) filings",
      "FATCA reporting (Form 8938)",
      "Form 5472 for foreign-owned US LLCs",
      "Form 5471 and 8865 for foreign entities",
      "ITIN applications (W-7)",
      "Tax-treaty analysis and Form 8833 positions",
    ],
    testimonial: {
      quote: "Every other CPA I called said 'we don't really do international.' Artha filed my back FBARs, cleaned up three years of 8938s, and got my streamlined-filing case accepted first-try.",
      author: "A. Volkov",
      role: "H-1B holder, previously non-compliant",
    },
    ctaText: "Get your cross-border compliance right",
  },
  "ai-finance": {
    icon: I.ai,
    eyebrow: "AI Finance",
    title: "AI Finance Implementation",
    tagline: "We design and deploy AI agents and automated workflows that eliminate manual finance tasks — from invoice processing to treasury management.",
    paragraphs: [
      "Most CPAs talk about AI. We implement it. Our team has deployed AI-first accounts-payable inboxes that read, code, and route invoices without a human touching them; reconciliation agents that clear 90%+ of transactions before a bookkeeper opens QuickBooks; and treasury workflows that sweep idle cash into higher-yield money-market funds automatically.",
      "Every implementation is documented, auditable, and handed back to your team with training — you own the workflow, not us. If we leave, your automation stays.",
    ],
    highlights: [
      "AI-powered AP inbox and invoice coding",
      "Automated bank reconciliation agents",
      "AR follow-up and collections workflows",
      "Treasury sweep and idle-cash automation",
      "Custom finance-team agents (Gemini, Claude, OpenAI)",
      "Full implementation documentation and training",
    ],
    testimonial: {
      quote: "The AP agent Artha built processes 400 invoices a week without our controller touching them. It's the single highest-ROI thing we've done all year.",
      author: "C. Fischer",
      role: "COO, industrial-services firm",
    },
    ctaText: "Automate your finance ops",
  },
};

const CATEGORY_LABEL = Object.fromEntries(
  (serviceMeta.categories || []).map((c) => [c.key, c.label])
);
const SERVICE_CATEGORY = Object.fromEntries(
  Object.entries(serviceMeta.services).map(([slug, s]) => [slug, s.category])
);

function CategoryChip({ slug }) {
  const cat = SERVICE_CATEGORY[slug];
  if (!cat || !CATEGORY_LABEL[cat]) return null;
  return (
    <Link
      to={`/?cat=${cat}#services`}
      className="sd-cat-chip"
      data-testid={`service-detail-category-chip-${cat}`}
      aria-label={`See all ${CATEGORY_LABEL[cat]} services`}
      title={`See all ${CATEGORY_LABEL[cat]} services`}
    >
      <span className="sd-cat-chip-dot" aria-hidden="true"></span>
      <span>{CATEGORY_LABEL[cat]}</span>
    </Link>
  );
}

function TrafficTicker({ slug }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/api/analytics/service-view-count?slug=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, [slug]);
  if (!data) return null;
  const hour = data.last_hour_unique || 0;
  const day = data.last_24h_unique || 0;
  let text = null;
  let window = null;
  if (hour >= 3) {
    text = `${hour} ${hour === 1 ? "person" : "people"} viewed this in the last hour`;
    window = "hour";
  } else if (day >= 5) {
    text = `${day} people viewed this in the last 24 hours`;
    window = "day";
  }
  if (!text) return null;
  return (
    <div className="sd-ticker" data-testid="traffic-ticker" data-window={window}>
      <span className="sd-ticker-dot" aria-hidden="true"></span>
      <span className="sd-ticker-label">Live</span>
      <span className="sd-ticker-text">{text}</span>
    </div>
  );
}

function QuoteForm({ slug, service }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      setErr("Please provide your first name and email.");
      return;
    }
    setStatus("sending");
    setErr("");
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          service: service.title,
          message: form.message.trim() || `Quote request from /services/${slug}`,
          source_page: `service_page:${slug}`,
          honeypot: "",
        }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Submission failed");
      setStatus("done");
      trackAdsConversion({ sourceService: slug });
    } catch (ex) {
      setStatus("error");
      setErr(String(ex.message || ex));
    }
  };

  if (status === "done") {
    return (
      <div className="sd-quote-card sd-quote-done" data-testid={`quote-success-${slug}`}>
        <div className="sd-quote-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
        <h3>You're on the list.</h3>
        <p>A CPA from our team will reach out about <strong>{service.title}</strong> within one business day. Want to talk sooner? Call <a href="tel:4698153491">(469) 815-3491</a>.</p>
      </div>
    );
  }

  return (
    <form className="sd-quote-card" onSubmit={onSubmit} data-testid={`quote-form-${slug}`}>
      <div className="sd-quote-head">
        <span className="al-eyebrow">Get a Quote</span>
        <h3>Talk to us about {service.title}</h3>
        <p>30-second form. A CPA replies within one business day. No sales pressure.</p>
      </div>
      <div className="sd-quote-row">
        <div className="sd-quote-field">
          <label htmlFor={`qf-first-${slug}`}>First name*</label>
          <input id={`qf-first-${slug}`} data-testid={`quote-first-${slug}`} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" required />
        </div>
        <div className="sd-quote-field">
          <label htmlFor={`qf-last-${slug}`}>Last name</label>
          <input id={`qf-last-${slug}`} data-testid={`quote-last-${slug}`} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
        </div>
      </div>
      <div className="sd-quote-row">
        <div className="sd-quote-field">
          <label htmlFor={`qf-email-${slug}`}>Email*</label>
          <input id={`qf-email-${slug}`} data-testid={`quote-email-${slug}`} type="email" value={form.email} onChange={set("email")} autoComplete="email" required />
        </div>
        <div className="sd-quote-field">
          <label htmlFor={`qf-phone-${slug}`}>Phone</label>
          <input id={`qf-phone-${slug}`} data-testid={`quote-phone-${slug}`} type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
      </div>
      <div className="sd-quote-field">
        <label htmlFor={`qf-msg-${slug}`}>Anything we should know?</label>
        <textarea id={`qf-msg-${slug}`} data-testid={`quote-msg-${slug}`} rows="2" value={form.message} onChange={set("message")} placeholder={`Optional — what's driving the need for ${service.title.toLowerCase()}?`} />
      </div>
      {err && <p className="sd-quote-err" role="alert">{err}</p>}
      <button type="submit" className="al-btn al-btn-primary sd-quote-submit" disabled={status === "sending"} data-testid={`quote-submit-${slug}`}>
        {status === "sending" ? "Sending…" : `Request a quote →`}
      </button>
      <p className="sd-quote-fine">By submitting you agree to be contacted about {service.title}. We never share your details.</p>
    </form>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const detail = SERVICE_DETAILS[slug];
  const seo = SEO[slug];
  const related = (RELATED[slug] || []).map((s) => SERVICE_DETAILS[s] && ({ slug: s, ...SERVICE_DETAILS[s] })).filter(Boolean);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Fire-and-forget service-view analytics ping
  useEffect(() => {
    if (!slug || !SERVICE_DETAILS[slug]) return;
    const ctrl = new AbortController();
    fetch(`${API}/api/analytics/service-view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, referrer: document.referrer || "" }),
      signal: ctrl.signal,
      keepalive: true,
    }).catch(() => {});
    return () => ctrl.abort();
  }, [slug]);

  useEffect(() => {
    if (!detail || !seo) return;
    const canonical = `${SITE_URL}/services/${slug}`;
    const prevTitle = document.title;
    document.title = seo.seoTitle;

    upsertMeta("name", "description", seo.seoDescription);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "robots", "index,follow");
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Artha Business Solutions LLC");
    upsertMeta("property", "og:title", seo.seoTitle);
    upsertMeta("property", "og:description", seo.seoDescription);
    upsertMeta("property", "og:url", canonical);
    const ogImage = `${SITE_URL}/og/${slug}.png`;
    upsertMeta("property", "og:image", ogImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", `${detail.title} — Artha Business Solutions LLC`);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.seoTitle);
    upsertMeta("name", "twitter:description", seo.seoDescription);
    upsertMeta("name", "twitter:image", ogImage);

    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${canonical}#service`,
          "name": detail.title,
          "description": seo.seoDescription,
          "url": canonical,
          "image": `${SITE_URL}/og/${slug}.png`,
          "serviceType": detail.title,
          "areaServed": [
            { "@type": "City", "name": "Frisco" },
            { "@type": "City", "name": "Dallas" },
            { "@type": "City", "name": "Fort Worth" },
            { "@type": "AdministrativeArea", "name": "Texas" },
            { "@type": "Country", "name": "United States" }
          ],
          "provider": {
            "@type": "AccountingService",
            "name": "Artha Business Solutions LLC",
            "url": SITE_URL,
            "telephone": "+1-469-815-3491",
            "email": "cpa@arthataxes.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "12800 Westridge Blvd, Suite 271",
              "addressLocality": "Frisco",
              "addressRegion": "TX",
              "postalCode": "75035",
              "addressCountry": "US"
            }
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": `${detail.title} — What's Included`,
            "itemListElement": detail.highlights.map((h, i) => ({
              "@type": "Offer",
              "position": i + 1,
              "itemOffered": { "@type": "Service", "name": h }
            }))
          },
          "review": {
            "@type": "Review",
            "reviewBody": detail.testimonial.quote,
            "author": { "@type": "Person", "name": detail.testimonial.author },
            "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${canonical}#breadcrumbs`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": `${SITE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": detail.title, "item": canonical }
          ]
        }
      ]
    };
    let ld = document.getElementById("sd-jsonld");
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "sd-jsonld";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(graph);

    return () => {
      document.title = prevTitle;
      const el = document.getElementById("sd-jsonld");
      if (el) el.remove();
    };
  }, [slug, detail, seo]);

  if (!detail) return <Navigate to="/" replace />;

  return (
    <div className="al-shell" data-testid={`service-detail-${slug}`}>
      <header className="al-nav">
        <div className="al-container">
          <div className="al-nav-inner">
            <Link to="/" className="al-nav-logo" aria-label="Artha Business Solutions — home">
              <img src="/artha-logo.png" alt="Artha" className="al-nav-logo-mark-img" />
              <div className="al-nav-brand">
                Artha Business Solutions LLC
                <small>Tax Advisory &amp; Preparation</small>
              </div>
            </Link>
            <ul className="al-nav-links">
              <li><Link to="/#services">Services</Link></li>
              <li><Link to="/#who-we-serve">Who We Serve</Link></li>
              <li><Link to="/#about">About</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
            </ul>
            <div className="al-nav-actions">
              <a href="tel:4698153491" className="al-btn al-btn-secondary" style={{ fontSize: 12, padding: "8px 16px" }}>(469) 815-3491</a>
              <Link to="/login" className="al-btn al-btn-secondary" data-testid="service-detail-portal-btn">Client Portal</Link>
              <Link to="/#contact" className="al-btn al-btn-primary" data-testid="service-detail-cta-nav">Schedule a Call</Link>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="sd-hero" aria-label={detail.title}>
          <div className="al-container">
            <Link to="/#services" className="sd-back" data-testid="service-detail-back">← All services</Link>
            <div className="sd-hero-inner">
              <div className="sd-hero-text">
                <TrafficTicker slug={slug} />
                <div className="sd-hero-eyebrow-row">
                  <span className="al-eyebrow">{detail.eyebrow}</span>
                  <CategoryChip slug={slug} />
                </div>
                <h1>{detail.title}</h1>
                <p className="sd-tagline">{detail.tagline}</p>
                <div className="sd-hero-btns">
                  <Link to="/#contact" className="al-btn al-btn-primary" data-testid="service-detail-primary-cta">{detail.ctaText}</Link>
                  <a href="tel:4698153491" className="al-btn al-btn-secondary">Call (469) 815-3491</a>
                </div>
              </div>
              <div className="sd-hero-icon" aria-hidden="true">{detail.icon}</div>
            </div>
          </div>
        </section>

        <section className="sd-body" aria-label="Service overview">
          <div className="al-container sd-body-grid">
            <div className="sd-body-copy">
              <span className="al-eyebrow">What we do</span>
              {detail.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <aside className="sd-body-side" aria-label="Highlights">
              <span className="al-eyebrow">What's included</span>
              <ul className="sd-highlights">
                {detail.highlights.map((h) => (
                  <li key={h}><Check /> <span>{h}</span></li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        <section className="sd-quote" aria-label="Get a quote">
          <div className="al-container">
            <QuoteForm slug={slug} service={detail} />
          </div>
        </section>

        <section className="sd-testimonial" aria-label="Client testimonial">
          <div className="al-container">
            <blockquote>
              <span className="sd-quote-mark" aria-hidden="true">“</span>
              <p>{detail.testimonial.quote}</p>
              <footer>
                <strong>{detail.testimonial.author}</strong>
                <span>{detail.testimonial.role}</span>
              </footer>
            </blockquote>
          </div>
        </section>

        {related.length > 0 && (
          <section className="sd-related" aria-label="Related services" data-testid="service-detail-related">
            <div className="al-container">
              <div className="sd-related-head">
                <span className="al-eyebrow">Related services</span>
                <h2>Keep exploring</h2>
                <p>Clients who engage us for {detail.title.toLowerCase()} often add these next.</p>
              </div>
              <div className="al-pill-stack in sd-related-stack">
                <ol className="al-pill-list" aria-label="Related services">
                  {related.map((r, i) => (
                    <li key={r.slug} style={{ listStyle: "none" }}>
                      <Link
                        to={`/services/${r.slug}`}
                        className="al-pill"
                        data-testid={`service-detail-related-pill-${i}`}
                        aria-label={r.title}
                      >
                        <div className="al-pill-badge-wrap">
                          <span className="al-pill-num">{String(i + 1).padStart(2, "0")}</span>
                          {r.icon && <span className="al-pill-icon" aria-hidden="true">{r.icon}</span>}
                        </div>
                        <div className="al-pill-body">
                          <p className="al-pill-title" title={r.title}>{r.title}</p>
                          <p className="al-pill-desc" title={PILL_DESC[r.slug]}>{PILL_DESC[r.slug]}</p>
                        </div>
                        <svg className="al-pill-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>
        )}

        <section className="sd-cta" aria-label="Get started">
          <div className="al-container sd-cta-inner">
            <div>
              <h2>Ready to move forward?</h2>
              <p>Book a 30-minute strategy session with a licensed CPA. No pressure, no cost — just a clear next step.</p>
            </div>
            <div className="sd-cta-btns">
              <Link to="/#contact" className="al-btn al-btn-primary" data-testid="service-detail-bottom-cta">{detail.ctaText}</Link>
              <a href="https://calendly.com/my-cpa/30" target="_blank" rel="noopener noreferrer" className="al-btn al-btn-outline" data-testid="service-detail-calendly">Book on Calendly →</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="al-footer" role="contentinfo">
        <div className="al-container">
          <div className="al-footer-grid">
            <div>
              <Link to="/" className="al-footer-logo" aria-label="Artha Business Solutions — home">
                <img src="/artha-logo.png" alt="Artha" className="al-footer-logo-mark-img" />
                <span className="al-footer-brand-name">Artha Business Solutions LLC</span>
              </Link>
              <address className="al-footer-bio" style={{ fontStyle: "normal" }}>
                Proactive financial strategy for growth-focused clients. Licensed CPAs serving founders, investors, and families globally.<br /><br />
                12800 Westridge Blvd, Suite 271<br />
                Frisco, Texas 75035<br />
                <a href="tel:4698153491">(469) 815-3491</a><br />
                <a href="mailto:cpa@arthataxes.com">cpa@arthataxes.com</a>
              </address>
            </div>
            <div>
              <div className="al-footer-col-title">Firm</div>
              <ul className="al-footer-links">
                <li><Link to="/#services">All Services</Link></li>
                <li><Link to="/#who-we-serve">Who We Serve</Link></li>
                <li><Link to="/#about">About</Link></li>
                <li><Link to="/#contact">Contact</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="al-footer-bottom">
            <p className="al-footer-copy">© {new Date().getFullYear()} Artha Business Solutions LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
